import { Columns, LogisticRegressionAnalysisV2 } from 'api/data/analyses';
import { isAnalysisInputValid } from 'helpers/analysis';
import {
	useAnalysesActiveColum,
	useFullscreenAnalysis,
	useFetchAnalysis,
	useTranslation
} from 'hooks/store';
import { useMediaQuery } from 'hooks/utils';
import { VariablesData } from 'store/data/variables';
import { ActionTypes, VariablesDataSelectItems } from 'store/data/analyses';
import { MediaQueries } from 'environment';

import { LogisticRegressionConfigV2 } from './LogisticRegressionConfigV2';
import { LogisticRegressionViewV2 } from './LogisticRegressionViewV2/LogisticRegressionViewV2';
import { AnalysisViewContainer } from '../UI';

import { InvalidAnalysis } from '..';
import { AnalysisContainer } from '../UI';
import { withMemo } from 'helpers/HOCs';
import { Loader } from 'components/UI/Loader';

interface Props {
	analysis: LogisticRegressionAnalysisV2;
	variablesDataSelectItems: VariablesDataSelectItems;
	variablesData: VariablesData;
	exporting: boolean;
}

function Component({ analysis, variablesData, variablesDataSelectItems, exporting }: Props) {
	const { translate } = useTranslation();
	const { loading } = useFetchAnalysis({
		actionType: ActionTypes.GET_LOGISTIC_REGRESSION_V2,
		id: analysis.id
	});

	const analysisValidInput = isAnalysisInputValid(analysis);
	const [activeColumn] = useAnalysesActiveColum();
	const [fullscreen, setFullscreenAnalysis] = useFullscreenAnalysis();

	const isMobileDevice = useMediaQuery(
		`only screen and ${MediaQueries.minWidth.xs} and ${MediaQueries.maxWidth.sm}`
	);

	const handleFullScreen = () => {
		if (
			(activeColumn !== Columns.OneColumn && !isMobileDevice) ||
			(activeColumn === Columns.OneColumn && isMobileDevice)
		) {
			setFullscreenAnalysis({ fullscreen: analysis.id });
		}
	};

	if (analysisValidInput) {
		return (
			<AnalysisContainer onClick={() => handleFullScreen()} activeColumn={activeColumn}>
				{!loading ? (
					<LogisticRegressionViewV2
						analysis={analysis}
						exporting={exporting}
						loading={loading}
						variablesData={variablesData}
					/>
				) : (
					<AnalysisViewContainer isConfigPanelOpen={analysis.options.configPanel.open}>
						<Loader primary />
					</AnalysisViewContainer>
				)}
				{(analysis.options.configPanel.open &&
					activeColumn === Columns.OneColumn &&
					!isMobileDevice) ||
				(analysis.options.configPanel.open && fullscreen) ? (
					<LogisticRegressionConfigV2
						analysis={analysis}
						variablesDataSelectItems={variablesDataSelectItems}
						loading={loading}
					/>
				) : null}
			</AnalysisContainer>
		);
	}

	return (
		<AnalysisContainer invalid>
			<InvalidAnalysis
				{...(!analysisValidInput
					? {
							headerMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputHeader
							)
					  }
					: {})}
				{...(!analysisValidInput
					? {
							bodyMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputBody
							)
					  }
					: {})}
				activeColumn={activeColumn}
				fullScreen={!!fullscreen}
				isConfigPanelOpen={analysis.options.configPanel.open}
				category
				numeric
			/>
			{(analysis.options.configPanel.open &&
				activeColumn === Columns.OneColumn &&
				!isMobileDevice) ||
			(analysis.options.configPanel.open && (analysis.activeTab === 1 || fullscreen)) ? (
				<LogisticRegressionConfigV2
					analysis={analysis}
					variablesDataSelectItems={variablesDataSelectItems}
					loading={loading}
				/>
			) : null}
		</AnalysisContainer>
	);
}

export const LogisticRegressionV2 = withMemo(Component, [
	'analysis',
	'variablesData',
	'variablesDataSelectItems',
	'exporting'
]);
